import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import usePageMeta from '../hooks/usePageMeta';

const services = [
    {
        id: 'interior-design',
        title: 'Interior Design',
        icon: '',
        tagline: 'Complete Home Transformation',
        image: 'https://images.unsplash.com/photo-1618219944342-824e40a13285?w=900&q=85',
        description: 'Our end-to-end interior design service takes your space from bare walls to a fully furnished, beautifully designed home. We manage everything — concept, 3D visualization, material selection, and execution.',
        subServices: [
            { title: '2BHK Interior Design', desc: 'Complete 2BHK design covering living room, kitchen, 2 bedrooms, and bathrooms with a unified design language.' },
            { title: '3BHK Interior Design', desc: 'Premium 3BHK interior design tailored for growing families who want luxury without compromising function.' },
            { title: 'Independent House Design', desc: 'Full-home design for independent houses — from ground floor to terrace, every space planned to perfection.' },
            { title: 'Duplex Interior Design', desc: 'Specialized duplex home interiors with split-level aesthetics, grand staircases, and cohesive design across both floors.' },
        ],
    },
    {
        id: 'modular-kitchen',
        title: 'Modular Kitchen',
        icon: '',
        tagline: 'Smart Kitchens for Modern Homes',
        image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=900&q=85',
        description: 'Custom modular kitchen solutions crafted with the finest materials — MDF, HDHMR, plywood, acrylics and laminates. Smart storage, ergonomic work triangles, and premium finishes guaranteed.',
        subServices: [
            { title: 'L-Shape Kitchen', desc: 'Perfect for medium-sized spaces. Creates an efficient work triangle and maximizes corner storage with smart solutions.' },
            { title: 'U-Shape Kitchen', desc: 'Ideal for larger kitchens. Wrap-around countertops provide ample workspace and storage on three sides.' },
            { title: 'Straight / Linear Kitchen', desc: 'Sleek single-wall kitchen design, ideal for studio apartments or open-plan living where space is at a premium.' },
            { title: 'Island Kitchen', desc: 'Open kitchen with a central island that adds prep space, doubles as a breakfast bar, and enhances social interaction.' },
            { title: 'Parallel Kitchen', desc: 'Two parallel runs of cabinets for high-traffic family kitchens, allowing two people to work comfortably.' },
        ],
    },
    {
        id: 'living-room',
        title: 'Living Room Design',
        icon: '',
        tagline: 'Spaces That Tell Your Story',
        image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=900&q=85',
        description: 'Your living room is the heart of your home — a place for family, guests, and relaxation. We design living spaces that are visually stunning, highly functional, and deeply personal to you.',
        subServices: [
            { title: 'TV Unit & Entertainment Wall', desc: 'Custom TV units with integrated storage, ambient back-lighting, and cable management for a clean, modern look.' },
            { title: 'Crockery Unit / Display Cabinet', desc: 'Statement crockery units that display your finest china and glassware while adding architectural interest.' },
            { title: 'Sofa & Seating Design', desc: 'Custom sofa configurations, recliners, and seating arrangements designed for your family\'s lifestyle.' },
            { title: 'Wall Paneling & Feature Walls', desc: 'Dramatic feature walls using fluted panels, veneer, wallpaper, cladding, or paint effects as a focal point.' },
            { title: 'Foyer & Entrance Design', desc: 'Grand entrance designs with console tables, mirrors, wall art, and ambient lighting for a strong first impression.' },
        ],
    },
    {
        id: 'bedroom-design',
        title: 'Bedroom Design',
        icon: '',
        tagline: 'Your Personal Sanctuary',
        image: 'https://images.unsplash.com/photo-1540518614846-7eded433c457?w=900&q=85',
        description: 'A bedroom should be a sanctuary — calm, restful, and deeply personal. Bliv Design Studio creates bedrooms that balance serene aesthetics with smart storage, premium materials, and bespoke furniture.',
        subServices: [
            { title: 'Master Bedroom Design', desc: 'Luxury master bedroom design with walk-in wardrobes, feature walls, ambient lighting, and premium finishes.' },
            { title: 'Kids Bedroom Design', desc: 'Fun, safe, and inspiring kids\' rooms that grow with your child — themed designs, smart storage, and study areas.' },
            { title: 'Guest Bedroom Design', desc: 'Comfortable, elegant guest bedrooms that make visitors feel at home with a touch of hotel-quality luxury.' },
            { title: 'Wardrobe & Storage Design', desc: 'Custom sliding and hinged wardrobes with internal organizers, trouser racks, and dedicated jewelry drawers.' },
            { title: 'Dresser & Vanity Units', desc: 'Bespoke dressing tables with mirror lighting, organized storage, and finishes that complement your bedroom theme.' },
        ],
    },
    {
        id: 'office-interior',
        title: 'Office Interior Design',
        icon: '',
        tagline: 'Spaces That Inspire Productivity',
        image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=85',
        description: 'We design commercial offices, co-working spaces, and home offices that boost productivity, reflect your brand identity, and create a positive employee experience through thoughtful space planning.',
        subServices: [
            { title: 'Corporate Office Design', desc: 'End-to-end corporate office interiors with conference rooms, workstations, reception areas, and breakout zones.' },
            { title: 'Home Office Setup', desc: 'Ergonomic home office designs with custom study tables, storage walls, and professional background aesthetics.' },
            { title: 'Co-working Space Design', desc: 'Flexible, vibrant co-working environments with hot desks, private cabins, lounge areas, and phone booths.' },
            { title: 'Retail & Showroom Design', desc: 'Brand-led retail interiors that drive foot traffic, showcase products effectively, and delight customers.' },
            { title: 'Healthcare Interior Design', desc: 'Functional, hygienic, and calming clinic and hospital interiors with infection-control compliant materials.' },
        ],
    },
];

export default function ServicesPage() {
    usePageMeta(
        'Interior Design Services | Modular Kitchen, Wardrobes, False Ceiling – Bliv Design Studio',
        'Explore Bliv Design Studio services across South India: end‑to‑end interiors, modular kitchens, wardrobes, false ceilings and more.'
    );
    const [searchParams] = useSearchParams();
    const [openSub, setOpenSub] = useState({});
    const [activeService, setActiveService] = useState(services[0].id);

    useEffect(() => {
        const id = searchParams.get('id');
        if (id && services.find(s => s.id === id)) {
            setActiveService(id);
            // Optional: Scroll to the element
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    }, [searchParams]);

    const toggleSub = (serviceId, subIdx) => {
        const key = `${serviceId}-${subIdx}`;
        setOpenSub(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const active = services.find(s => s.id === activeService);

    return (
        <>
            {/* Hero */}
            <section className="relative pt-36 pb-24 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#1C2541] to-[#0D0D0D]" />
                <div className="absolute inset-0 opacity-10">
                    <img src="https://images.unsplash.com/photo-1618219944342-824e40a13285?w=1920&q=80" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    <span className="inline-block bg-[#2E63E8]/20 border border-[#2E63E8]/40 text-[#2E63E8] text-xs font-semibold px-5 py-2 rounded-full mb-5 uppercase tracking-widest">
                        What We Offer
                    </span>
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-5 leading-tight">Our Services</h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                        From concept to completion — comprehensive interior design solutions for every space, style, and budget.
                    </p>
                </div>
            </section>

            {/* Service Tabs */}
            <section className="bg-white sticky top-[105px] z-40 border-b border-gray-100 shadow-sm">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex overflow-x-auto gap-2 py-3 no-scrollbar">
                        {services.map(s => (
                            <button
                                key={s.id}
                                onClick={() => setActiveService(s.id)}
                                className={`flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200
                  ${activeService === s.id ? 'bg-[#2E63E8] text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                {s.title}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Details */}
            <section className="section-pad bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    {services.map((service, idx) => (
                        <div
                            key={service.id}
                            id={service.id}
                            className={`mb-20 pb-20 border-b border-gray-100 last:border-0 last:mb-0 ${activeService === service.id ? 'scroll-mt-40' : ''}`}
                        >
                            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-14 items-center ${idx % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                                {/* Image */}
                                <div className={`${idx % 2 !== 0 ? 'lg:order-2' : ''}`}>
                                    <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                                        <img
                                            src={service.image}
                                            alt={service.title}
                                            className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                        <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2">
                                            <span className="text-[#2E63E8] font-bold text-sm">{service.tagline}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className={`${idx % 2 !== 0 ? 'lg:order-1' : ''}`}>
                                    <span className="text-[#2E63E8] font-semibold text-xs uppercase tracking-widest">Service {String(idx + 1).padStart(2, '0')}</span>
                                    <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4">{service.title}</h2>
                                    <p className="text-gray-600 leading-relaxed mb-7">{service.description}</p>

                                    {/* Sub-services Accordion */}
                                    <div className="space-y-2 mb-8">
                                        <p className="text-gray-800 font-bold text-sm uppercase tracking-widest mb-3">Sub-Services</p>
                                        {service.subServices.map((sub, subIdx) => {
                                            const key = `${service.id}-${subIdx}`;
                                            const isOpen = openSub[key];
                                            return (
                                                <div key={subIdx} className={`rounded-xl border transition-all duration-300 ${isOpen ? 'border-[#2E63E8]' : 'border-gray-200'}`}>
                                                    <button
                                                        onClick={() => toggleSub(service.id, subIdx)}
                                                        className="w-full flex items-center justify-between px-5 py-3.5 text-left"
                                                    >
                                                        <span className={`font-semibold text-sm ${isOpen ? 'text-[#2E63E8]' : 'text-gray-800'}`}>
                                                            {sub.title}
                                                        </span>
                                                        <span className={`text-xl font-light ml-4 flex-shrink-0 transition-colors ${isOpen ? 'text-[#2E63E8]' : 'text-gray-400'}`}>
                                                            {isOpen ? '−' : '+'}
                                                        </span>
                                                    </button>
                                                    <div className={`accordion-content ${isOpen ? 'open' : ''}`}>
                                                        <p className="px-5 pb-4 text-gray-500 text-sm leading-relaxed">{sub.desc}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* CTA */}
                                    <div className="flex gap-3 flex-wrap">
                                        <a href="/contact" className="btn-yellow px-7 py-3 text-sm inline-block">
                                            Get Free Quote →
                                        </a>
                                        <a href="/contact" className="border-2 border-[#2E63E8] text-[#2E63E8] hover:bg-[#2E63E8] hover:text-white font-bold px-7 py-3 rounded-lg transition-all duration-300 text-sm inline-block">
                                            Book Consultation
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Bottom Banner */}
            <section className="bg-[#f0f4fe] py-16 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Not Sure Which Service You Need?</h3>
                    <p className="text-gray-600 mb-8">Our designers offer a free 30-minute consultation to assess your space and recommend the perfect solution for your budget.</p>
                    <a href="/contact" className="btn-yellow inline-block px-10 py-4 text-base">
                        Book a Free Design Session
                    </a>
                </div>
            </section>
        </>
    );
}
